import posthog, { type BeforeSendFn, type Properties } from 'posthog-js';
import {
    AnalyticsEventMap,
    AnalyticsEventName,
    BANNED_PROPERTY_PATTERNS,
    BlockedSurface,
    PlanKey,
    ReportFailureKind,
} from './analytics-events';
import { isPatientFacingRoute, matchRoutePattern, UNKNOWN_ROUTE } from './route-patterns';
import type { EntitlementDecision } from '@/api/entitlements';

/**
 * Product analytics client.
 *
 * This module is the only place allowed to import `posthog-js`. Everything
 * else goes through `capture()`, which accepts only registry events and strips
 * anything that looks like personal data on the way out.
 *
 * The rules this enforces are documented in docs/analytics.md. The short
 * version: we record which screens people reach and which buttons they press,
 * and nothing else. No typed input, no clinical content, no patient or report
 * identifiers, no raw URLs.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

/**
 * Disabled without a key, so local dev and the Playwright harness make no
 * network calls and need no configuration. Also hard-off under `test` so an
 * accidentally-present key can never make the E2E suite emit events.
 */
const isConfigured = Boolean(POSTHOG_KEY) && import.meta.env.MODE !== 'test';

/** Person properties we are willing to store. Anything else is dropped (R1). */
const ALLOWED_PERSON_PROPERTIES = ['is_internal', 'plan', 'signup_month'];

/**
 * Property names PostHog populates itself that can contain a full URL — and
 * our URLs embed patient and report UUIDs. Matched loosely on purpose, so a
 * future SDK version adding `$some_new_url` is sanitized too, not leaked.
 */
const URL_PROPERTY_PATTERN = /(url|pathname|referrer)/i;

/** Guards against a free-text value slipping in as an enum-looking property. */
const MAX_PROPERTY_LENGTH = 64;

let isInitialized = false;

const stripQueryAndHash = (path: string) => path.split('?')[0].split('#')[0];

/**
 * Rewrites any URL-ish value to something that cannot identify a patient:
 * same-origin URLs keep their origin but lose the concrete path, external
 * referrers keep only their origin.
 */
const sanitizeUrlValue = (value: unknown): unknown => {
    if (typeof value !== 'string' || value === '') return value;

    if (value.startsWith('/')) {
        return matchRoutePattern(stripQueryAndHash(value));
    }

    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return UNKNOWN_ROUTE;
    }

    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
        return `${url.origin}${matchRoutePattern(url.pathname)}`;
    }

    // An external referrer's domain is useful for attribution; its path and
    // query are not ours to forward.
    return url.origin;
};

const sanitizeUrlProperties = (properties?: Properties) => {
    if (!properties) return;

    for (const key of Object.keys(properties)) {
        if (URL_PROPERTY_PATTERN.test(key)) {
            properties[key] = sanitizeUrlValue(properties[key]);
        }
    }
};

/** Person properties are allowlisted; PostHog's own `$`-prefixed keys pass. */
const sanitizePersonProperties = (properties?: Properties) => {
    if (!properties) return;

    for (const key of Object.keys(properties)) {
        if (!key.startsWith('$') && !ALLOWED_PERSON_PROPERTIES.includes(key)) {
            delete properties[key];
        }
    }
};

const beforeSend: BeforeSendFn = (event) => {
    if (!event) return event;

    sanitizeUrlProperties(event.properties);
    sanitizeUrlProperties(event.$set);
    sanitizeUrlProperties(event.$set_once);
    sanitizePersonProperties(event.$set);
    sanitizePersonProperties(event.$set_once);

    return event;
};

/**
 * Runtime backstop for the typed registry. TypeScript already rejects unknown
 * events and stray properties, but plain-JS call sites and future refactors
 * don't get that check, and the cost of a leak here is patient data.
 */
const sanitizeEventProperties = (event: string, properties: Properties): Properties => {
    const safe: Properties = {};

    for (const [key, value] of Object.entries(properties)) {
        if (BANNED_PROPERTY_PATTERNS.some((pattern) => pattern.test(key))) {
            if (import.meta.env.DEV) {
                console.warn(`[analytics] dropped property "${key}" on "${event}": banned name`);
            }
            continue;
        }

        const isScalar =
            typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string';

        if (!isScalar) {
            if (import.meta.env.DEV) {
                console.warn(
                    `[analytics] dropped property "${key}" on "${event}": values must be a boolean, number, or known string`,
                );
            }
            continue;
        }

        if (typeof value === 'string' && value.length > MAX_PROPERTY_LENGTH) {
            if (import.meta.env.DEV) {
                console.warn(
                    `[analytics] dropped property "${key}" on "${event}": string too long to be an enum`,
                );
            }
            continue;
        }

        safe[key] = value;
    }

    return safe;
};

/** Patients answering an intake form are data subjects, not customers (R6). */
const isTrackableRoute = () =>
    typeof window !== 'undefined' && !isPatientFacingRoute(window.location.pathname);

export const initAnalytics = () => {
    if (isInitialized || !isConfigured || !POSTHOG_KEY || !isTrackableRoute()) return;

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: '2026-06-25',

        // Every flag below is set explicitly rather than inherited from
        // `defaults`, so bumping the defaults date can never silently turn
        // data collection back on.

        // Autocapture would record button labels and input names — i.e. things
        // the user typed and things we never reviewed. Every event is
        // deliberate and declared in analytics-events.ts. This is also the
        // main reason our event volume stays inside the free tier.
        autocapture: false,
        rageclick: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,

        // The report editor renders patient clinical data. Never record it.
        disable_session_recording: true,
        disable_surveys: true,

        // Pageviews are emitted by PageTitle() with the route *pattern*.
        // PostHog's built-in version would send the raw URL, UUIDs and all.
        capture_pageview: false,
        capture_pageleave: false,

        // Anonymous visitors get no person profile; identity starts at login.
        person_profiles: 'identified_only',

        before_send: beforeSend,
    });

    isInitialized = true;
};

/**
 * Emits a registry event. The generic ties each event name to its own property
 * shape, so an unknown event or an unexpected property fails to compile.
 */
export const capture = <K extends AnalyticsEventName>(
    event: K,
    properties: AnalyticsEventMap[K],
) => {
    if (!isInitialized || !isTrackableRoute()) return;

    posthog.capture(event, sanitizeEventProperties(event, properties as Properties));
};

/**
 * Emits `feature_blocked` from an entitlement decision.
 *
 * The decision already carries a closed `feature` and `reason` from the API, so
 * this exists to keep the ten-odd `shouldBlock` branches in the report editor
 * one line each — and to make sure none of them invents its own property names.
 * A decision that allowed the action, or that carries no reason, emits nothing.
 */
export const captureFeatureBlocked = (
    surface: BlockedSurface,
    decision: EntitlementDecision | undefined,
) => {
    if (!decision || decision.allowed || !decision.reason) return;

    capture('feature_blocked', {
        feature_key: decision.feature,
        denial_reason: decision.reason,
        surface,
    });
};

/**
 * Buckets a failed API request by HTTP status alone.
 *
 * The message never travels: API errors can quote back what the user typed, and
 * the status answers "was this us, them, or the connection" just as well. Only
 * call this from a catch around a request — a request that never got a status
 * is reported as `network`, which is misleading for a plain runtime error.
 */
export const toFailureKind = (error: unknown): ReportFailureKind => {
    const apiError = error as { response?: { status?: number }; status?: number } | null;
    const status = apiError?.response?.status ?? apiError?.status;

    if (status === undefined) return 'network';
    if (status === 401 || status === 403) return 'permission';
    if (status === 429) return 'quota';
    // A 400 on save means the API refused the payload's shape: front and API
    // disagree on the contract, and nothing the user does will fix it.
    if (status === 400 || status === 422) return 'validation';
    if (status >= 500) return 'server';

    return 'unknown';
};

export const identifyUser = (
    userId: string,
    properties: { is_internal: boolean; plan?: PlanKey },
) => {
    if (!isInitialized || !userId) return;

    posthog.identify(userId, {
        is_internal: properties.is_internal,
        ...(properties.plan ? { plan: properties.plan } : {}),
    });
};

export const resetUser = () => {
    if (!isInitialized) return;

    posthog.reset();
};

/** Exported for the privacy guard tests. */
export const __testing = {
    sanitizeUrlValue,
    sanitizeEventProperties,
    sanitizePersonProperties,
    beforeSend,
};
